/**
 * HeroBestsellers — compact vertical product slider rendered in the hero's
 * right column when the admin sets `layout.hero_right_content` to "bestsellers".
 *
 * Pulls the same product picks as FeaturedProducts (one per category, then
 * tagged extras) so the data source is consistent. Renders as a compact
 * vertical card stack with internal scroll, fitting any hero panel height.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, ChevronRight } from 'lucide-react';
import { useCategories } from '../context/CategoriesContext';
import { useSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';
import { resolveImageUrl } from './ImageUpload';
import { priceWithTier } from '../lib/pricing';
import Rial from './Rial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroBestsellers = ({ className = '' }) => {
  const [products, setProducts] = useState([]);
  const { categories } = useCategories();
  const { settings } = useSettings();
  const { add } = useCart();
  const { discountPct, priceOverrides } = useCustomerAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!categories.length) return;
    axios.get(`${API}/products`).then(({ data }) => {
      const picks = [];
      // Strict mode: only tagged products. Same rule as FeaturedProducts so
      // the standalone section and this hero-column slider stay consistent.
      categories.forEach((c) => {
        const inCat = data.filter((p) => p.category_slug === c.slug);
        const tagged = inCat.find((p) => p.tag);
        if (tagged) picks.push({ ...tagged, _category: c });
      });
      const extras = data
        .filter((p) => p.tag && !picks.find((x) => x.id === p.id))
        .slice(0, 8);
      extras.forEach((p) => {
        const cat = categories.find((c) => c.slug === p.category_slug);
        if (cat) picks.push({ ...p, _category: cat });
      });
      // Promote "Coming Soon" to the top of the bestsellers strip, demote
      // out-of-stock to the bottom.
      const rank = (p) => {
        const s = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
        return s === 'coming_soon' ? 0 : s === 'in_stock' ? 1 : 2;
      };
      picks.sort((a, b) => rank(a) - rank(b));
      setProducts(picks.slice(0, 10));
    }).catch(() => {});
  }, [categories]);

  const handleAdd = (p) => {
    const status = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
    if (status === 'coming_soon') {
      toast({ title: 'Coming soon', description: `${p.name} isn’t available yet.` });
      return;
    }
    if (status === 'out_of_stock' || p.in_stock === false) {
      toast({ title: 'Out of stock', description: `${p.name} is currently unavailable.` });
      return;
    }
    const uom = p.uom || 'ctn';
    const minQty = p.min_qty || 1;
    add(
      {
        id: p.id,
        name: p.name,
        price: priceWithTier(p.price, discountPct, priceOverrides?.[p.id]),
        retail_price: p.price,
        size: p.size,
        size_type: p.size_type || 'size',
        image: resolveImageUrl(p.image) || resolveImageUrl(p._category.image),
        bg: p._category.bg,
        uom,
        min_qty: minQty,
        vat_applicable: !!p.vat_applicable,
      },
      minQty,
    );
    toast({ title: 'Added to cart', description: `${minQty} ${uom} × ${p.name}` });
  };

  if (products.length === 0) return null;

  return (
    <div
      className={`layout-hero-image-col relative rounded-3xl bg-white border border-slate-100 overflow-hidden flex flex-col ${className}`}
      data-testid="hero-bestsellers"
    >
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-2 border-b border-slate-100 shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] font-bold tracking-[0.2em] text-[#0078D4] uppercase">
            {settings?.featured_section_eyebrow || 'Bestsellers'}
          </div>
          <div className="font-display text-sm sm:text-base text-slate-900 mt-0.5 truncate">
            {settings?.featured_section_heading || 'Top moving SKUs'}
          </div>
        </div>
        <Link
          to="/"
          className="text-[10px] font-semibold text-[#0078D4] hover:text-[#005A9E] flex items-center gap-0.5 shrink-0"
        >
          All <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2 scrollbar-hide">
        {products.map((p) => {
          const finalPrice = priceWithTier(p.price, discountPct, priceOverrides?.[p.id]);
          const img = resolveImageUrl(p.image) || resolveImageUrl(p._category.image);
          const status = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
          const comingSoon = status === 'coming_soon';
          const outOfStock = status === 'out_of_stock' || (p.in_stock === false && !comingSoon);
          const unavailable = comingSoon || outOfStock;
          return (
            <div
              key={p.id}
              className={`group flex items-center gap-3 p-2 rounded-xl bg-slate-50/60 hover:bg-white hover:shadow-md border border-transparent hover:border-slate-200 transition ${unavailable ? 'opacity-80' : ''}`}
              data-testid={`hero-best-card-${p.id}`}
            >
              <div
                className="w-14 h-14 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden relative"
                style={{ background: p._category.bg || '#F1F5F9' }}
              >
                {img && (
                  <img src={img} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
                )}
                {comingSoon && (
                  <span className="absolute bottom-0 left-0 right-0 text-[7px] font-bold uppercase tracking-wider bg-amber-500 text-white text-center py-0.5">Soon</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold tracking-wider text-slate-400 uppercase truncate">
                  {p._category.name}
                </div>
                <div className="text-xs font-semibold text-slate-900 leading-tight line-clamp-2">
                  {p.name}
                </div>
                <div className="text-[11px] font-bold text-[#0078D4] mt-0.5">
                  <Rial /> {finalPrice.toFixed(3)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleAdd(p)}
                disabled={unavailable}
                className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center hover:bg-[#0078D4] transition shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Add ${p.name}`}
                data-testid={`hero-best-add-${p.id}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HeroBestsellers;
